import type { ApiClient } from '../api/client';
import { META, readMeta, writeMeta } from '../db/schema';
import type { AppDb, MirrorWindow, MirroredAppointment } from '../db/schema';
import {
  Appointment,
  AppointmentDay,
  BlackoutList,
  ClientDetail,
  ClientList,
  Hours,
  ResourceList,
  ServiceList,
  Session,
} from '../wire';
import type {
  Blackout,
  ClientDetail as ClientDetailType,
  Hours as HoursType,
  Resource,
  Service,
  Session as SessionType,
} from '../wire';
import { addDays, dayDistance, isoDayIn } from './time';
import { ApiFailure } from '../api/errors';

/**
 * The mirror — spec §8.1.
 *
 * > The PWA mirrors the tenant's working set — today plus a window of
 * > appointments, the client book, services, products, open certificates — into
 * > local storage, refreshed on connect and by SSE while online. Offline, every
 * > read is served from the mirror and the UI says plainly how stale it is.
 *
 * Two rules hold this together and both are enforced by the shape of the class
 * rather than by care:
 *
 * **Every read in the app goes through here, and none of them touch the
 * network.** `today()`, `clients()`, `services()` and the rest read IndexedDB and
 * only IndexedDB. There is no "fetch if online" fallback, because a fallback is
 * a code path that is only exercised when there *is* a network — which is the
 * condition under which offline bugs are invisible. The views cannot tell
 * whether the iPad has a connection, which is why they cannot break when it
 * does not.
 *
 * **Freshness has exactly two sources: `hydrate` and `apply`.** Hydrate is the
 * connect-time refill. Apply is one SSE event turning into one targeted refetch.
 * Both stamp `lastSyncAt`, which is the number the status strip renders, so the
 * staleness the owner reads is the real age of the newest fact on the device and
 * not a timer that ticks whether or not anything arrived.
 */

export interface MirrorOptions {
  db: AppDb;
  api: ApiClient;
  onChange: () => void;
  now?: () => number;
  pastDays?: number;
  futureDays?: number;
  /** Must stay under `core-api`'s `CALENDAR_MAX_WINDOW_DAYS` (default 31). */
  chunkDays?: number;
}

export interface MirrorState {
  session: SessionType | null;
  lastSyncAt: string | null;
  cursor: string | null;
  window: MirrorWindow | null;
}

export class Mirror {
  private readonly db: AppDb;
  private readonly api: ApiClient;
  private readonly onChange: () => void;
  private readonly now: () => number;
  private readonly pastDays: number;
  private readonly futureDays: number;
  private readonly chunkDays: number;

  constructor(options: MirrorOptions) {
    this.db = options.db;
    this.api = options.api;
    this.onChange = options.onChange;
    this.now = options.now ?? Date.now;
    this.pastDays = options.pastDays ?? 7;
    this.futureDays = options.futureDays ?? 60;
    this.chunkDays = options.chunkDays ?? 28;
  }

  /* ---------------------------------------------------------------- */
  /* Filling it                                                        */
  /* ---------------------------------------------------------------- */

  /**
   * Refill the whole working set. Called on sign-in and on every reconnect.
   *
   * The order is deliberate: the session first, because its `timezone` is what
   * every appointment's day index is computed in and a book indexed in the
   * wrong zone would put an 11 PM booking on the wrong day of the calendar.
   */
  async hydrate(): Promise<void> {
    const session = await this.api.request({
      method: 'GET',
      path: '/session',
      schema: Session,
    });
    await writeMeta(this.db, META.session, session);

    const timezone = session.tenant.timezone;
    const today = isoDayIn(timezone, this.now());
    const from = addDays(today, -this.pastDays);
    const to = addDays(today, this.futureDays);

    await Promise.all([
      this.refreshCatalog(),
      this.refreshClients(),
      this.refreshAppointments(from, to, timezone),
    ]);

    await writeMeta(this.db, META.window, { from, to } satisfies MirrorWindow);
    await this.touch();
  }

  async refreshCatalog(): Promise<void> {
    const [services, resources, hours, blackouts] = await Promise.all([
      this.api.request({ method: 'GET', path: '/services', schema: ServiceList }),
      this.api.request({ method: 'GET', path: '/resources', schema: ResourceList }),
      this.api.request({ method: 'GET', path: '/settings/hours', schema: Hours }),
      this.api.request({ method: 'GET', path: '/blackouts', schema: BlackoutList }),
    ]);

    const tx = this.db.transaction(['services', 'resources', 'blackouts'], 'readwrite');
    await tx.objectStore('services').clear();
    await tx.objectStore('resources').clear();
    await tx.objectStore('blackouts').clear();
    for (const service of services.services) await tx.objectStore('services').put(service);
    for (const resource of resources.resources) await tx.objectStore('resources').put(resource);
    for (const blackout of blackouts.blackouts) await tx.objectStore('blackouts').put(blackout);
    await tx.done;

    await writeMeta(this.db, META.hours, hours);
    await this.touch();
  }

  async refreshClients(): Promise<void> {
    const list = await this.api.request({
      method: 'GET',
      path: '/clients',
      query: { limit: 500, include_archived: 'true' },
      schema: ClientList,
    });

    // The list endpoint returns summaries; the mirror stores details, because
    // the client screen must open offline. A summary is upgraded in place when
    // a detail is fetched, and a summary already in the mirror is never
    // *downgraded* by a later list — that would drop preferences the device has.
    const tx = this.db.transaction('clients', 'readwrite');
    for (const summary of list.clients) {
      const existing = await tx.store.get(summary.client_id);
      const merged: ClientDetailType = {
        ...(existing ?? emptyDetail(summary.client_id)),
        ...summary,
      };
      await tx.store.put(merged);
    }
    await tx.done;
    await this.touch();
  }

  /**
   * The book, paged.
   *
   * `core-api` **clamps** an over-wide window rather than rejecting it (it is
   * the same choice `provider-api` makes, and it is friendlier to a caller that
   * asks for a year). It also means a device that asked for 68 days and did not
   * notice would mirror the first 31 and believe it had the lot, showing an
   * empty calendar for October rather than an unknown one. So the window is cut
   * into chunks smaller than the clamp, here, where the arithmetic is visible.
   */
  async refreshAppointments(from: string, to: string, timezone: string): Promise<void> {
    const days: MirroredAppointment[] = [];
    let cursor = from;
    while (dayDistance(cursor, to) >= 0) {
      const chunkTo = minDay(addDays(cursor, this.chunkDays - 1), to);
      const page = await this.api.request({
        method: 'GET',
        path: '/appointments',
        query: { date_from: cursor, date_to: chunkTo },
        schema: AppointmentDay,
      });
      for (const appointment of page.appointments) {
        days.push({ ...appointment, day: isoDayIn(timezone, appointment.slot.start) });
      }
      cursor = addDays(chunkTo, 1);
    }

    const tx = this.db.transaction('appointments', 'readwrite');
    // Replace the window wholesale rather than upserting: a cancellation that
    // happened while the device was away is an *absence*, and an upsert cannot
    // represent an absence. The window is bounded, so this is cheap and it is
    // the only way the mirror converges on the server's book rather than on the
    // union of every book it has ever seen.
    for (const existing of await tx.store.getAll()) {
      if (dayDistance(from, existing.day) >= 0 && dayDistance(existing.day, to) >= 0) {
        await tx.store.delete(existing.appointment_id);
      }
    }
    for (const appointment of days) await tx.store.put(appointment);
    await tx.done;
    await this.touch();
  }

  /* ---------------------------------------------------------------- */
  /* Keeping it fresh: one event, one targeted refetch                 */
  /* ---------------------------------------------------------------- */

  /**
   * Apply one event from the stream.
   *
   * Refetch rather than apply-the-payload, on purpose. The event payloads are
   * the *partner's* shapes (`serializeBooking` — the webhook body), not the
   * staff projection: no client name, no service name, no resource name, no
   * notes. Writing one into the mirror would put a row on the calendar that
   * renders as a blank line. The event is a **cue**, and one extra small GET on
   * a change somebody just made is a cost the plan's own acceptance —
   * *"two devices on the same tenant never see different books while online"* —
   * is happy to pay.
   */
  async apply(type: string, subjectId: string | null): Promise<void> {
    if (type.startsWith('booking.') && subjectId !== null) {
      await this.refetchAppointment(subjectId);
    } else if (type.startsWith('client.') && subjectId !== null) {
      await this.refetchClient(subjectId);
    } else if (type === 'catalog.updated') {
      await this.refreshCatalog();
    }
    await this.touch();
  }

  async refetchAppointment(appointmentId: string): Promise<void> {
    const session = await this.session();
    if (session === null) return;
    try {
      const appointment = await this.api.request({
        method: 'GET',
        path: `/appointments/${appointmentId}`,
        schema: Appointment,
      });
      await this.putAppointment(appointment, session.tenant.timezone);
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'not_found') {
        await this.db.delete('appointments', appointmentId);
        this.onChange();
        return;
      }
      throw e;
    }
  }

  async refetchClient(clientId: string): Promise<void> {
    const client = await this.api.request({
      method: 'GET',
      path: `/clients/${clientId}`,
      schema: ClientDetail,
    });
    await this.putClient(client);
  }

  /* ---------------------------------------------------------------- */
  /* Local writes — what a settled intent does to the mirror           */
  /* ---------------------------------------------------------------- */

  async putAppointment(appointment: Appointment, timezone: string): Promise<void> {
    await this.db.put('appointments', {
      ...appointment,
      day: isoDayIn(timezone, appointment.slot.start),
    });
    this.onChange();
  }

  async putClient(client: ClientDetailType): Promise<void> {
    await this.db.put('clients', client);
    this.onChange();
  }

  async putService(service: Service): Promise<void> {
    await this.db.put('services', service);
    this.onChange();
  }

  async putBlackout(blackout: Blackout): Promise<void> {
    await this.db.put('blackouts', blackout);
    this.onChange();
  }

  async putHours(hours: HoursType): Promise<void> {
    await writeMeta(this.db, META.hours, hours);
    this.onChange();
  }

  /* ---------------------------------------------------------------- */
  /* Reads. Local, always.                                             */
  /* ---------------------------------------------------------------- */

  async session(): Promise<SessionType | null> {
    return (await readMeta<SessionType>(this.db, META.session)) ?? null;
  }

  async state(): Promise<MirrorState> {
    const [session, lastSyncAt, cursor, window] = await Promise.all([
      readMeta<SessionType>(this.db, META.session),
      readMeta<string>(this.db, META.lastSyncAt),
      readMeta<string>(this.db, META.cursor),
      readMeta<MirrorWindow>(this.db, META.window),
    ]);
    return {
      session: session ?? null,
      lastSyncAt: lastSyncAt ?? null,
      cursor: cursor ?? null,
      window: window ?? null,
    };
  }

  async day(isoDay: string): Promise<MirroredAppointment[]> {
    const rows = await this.db.getAllFromIndex('appointments', 'by-day', isoDay);
    return rows.sort((a, b) => a.slot.start.localeCompare(b.slot.start));
  }

  async appointment(id: string): Promise<MirroredAppointment | undefined> {
    return this.db.get('appointments', id);
  }

  async clients(search?: string): Promise<ClientDetailType[]> {
    const rows = await this.db.getAll('clients');
    const needle = (search ?? '').trim().toLowerCase();
    const matched =
      needle === ''
        ? rows
        : rows.filter(
            (c) =>
              c.display_name.toLowerCase().includes(needle) ||
              c.identities.some((i) => i.identity.toLowerCase().includes(needle)),
          );
    return matched.sort((a, b) => a.display_name.localeCompare(b.display_name));
  }

  async client(id: string): Promise<ClientDetailType | undefined> {
    return this.db.get('clients', id);
  }

  async services(): Promise<Service[]> {
    const rows = await this.db.getAll('services');
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }

  async resources(): Promise<Resource[]> {
    const rows = await this.db.getAll('resources');
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }

  async blackouts(): Promise<Blackout[]> {
    const rows = await this.db.getAll('blackouts');
    return rows.sort((a, b) => a.start.localeCompare(b.start));
  }

  async hours(): Promise<HoursType | null> {
    return (await readMeta<HoursType>(this.db, META.hours)) ?? null;
  }

  async cursor(): Promise<string | null> {
    return (await readMeta<string>(this.db, META.cursor)) ?? null;
  }

  async setCursor(cursor: string): Promise<void> {
    await writeMeta(this.db, META.cursor, cursor);
  }

  /** Stamp "the newest fact on this device arrived now". */
  async touch(): Promise<void> {
    await writeMeta(this.db, META.lastSyncAt, new Date(this.now()).toISOString());
    this.onChange();
  }
}

/** The earlier of two days. `dayDistance(a, b) >= 0` means `a` is not after `b`. */
function minDay(a: string, b: string): string {
  return dayDistance(a, b) >= 0 ? a : b;
}

function emptyDetail(clientId: string): ClientDetailType {
  return {
    client_id: clientId,
    display_name: '',
    archived: false,
    is_walk_in_account: false,
    tags: [],
    identities: [],
    visit_count: 0,
    last_visit_at: null,
    birthday: null,
    notes: null,
    sms_opt_in: false,
    preferences: [],
    anonymized_at: null,
    created_at: new Date(0).toISOString(),
    updated_at: new Date(0).toISOString(),
  };
}
